import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react'
import {
  ArrowLeft,
  ArrowRight,
  Clock,
  Send,
  Flag,
  Check,
  X,
  Volume2,
  Settings,
  HelpCircle,
  BarChart3,
  Target,
  CheckCircle,
  AlertTriangle,
  Maximize,
  Minimize,
  User,
  Menu,
  List,
  Monitor
} from 'lucide-react'
import toast from 'react-hot-toast'
import QuizSubmission from './QuizSubmission'
import QuizQuestionCard from './QuizQuestionCard'
import KaTeXRenderer from '../common/KaTeXRenderer'
import TrueFalseQuestion from './TrueFalseQuestion'
import { useAuth } from '../../contexts/AuthContext'
import { useQuizTracking } from '../../hooks/useQuizTracking'
import { useFullscreenQuiz } from '../../hooks/useFullscreenQuiz'
import AchievementNotification from '../achievement/AchievementNotification'
import { QuizAnswer, Achievement } from '../../types/learningHistory'
import { imageExtractor } from '../../services/teacher/ImageExtractor'
import { emailServicePHP } from '../../services/email/EmailServicePHP'
import { QuizScoringService, ScoringResult } from '../../services/quiz/QuizScoringService'
import { autoCoringService, GradeResult } from '../../services/auto-coring/AutoCoringService'
import { API_CONFIG } from '../../config/api.config'

type QuestionGroup = 'TN' | 'DS' | 'TL' | 'TLU' | 'OTHER'
const GROUP_TITLES: Record<QuestionGroup, string> = {
  TN: 'Phần I - Trắc nghiệm',
  DS: 'Phần II - Đúng/Sai',
  TL: 'Phần III - Trả lời ngắn',
  TLU: 'Phần IV - Tự luận',
  OTHER: 'Các câu hỏi khác'
}
const GROUP_FALLBACK_BY_TYPE: Record<string, QuestionGroup> = {
  trac_nghiem_nhieu_lua_chon: 'TN',
  multiple_choice: 'TN',
  dung_sai: 'DS',
  true_false: 'DS',
  tinh_toan: 'TL',
  short_answer: 'TL'
}

interface QuizInterfaceProps {
  quizData: any
  lessonId: string
  onBack: () => void
  onFullscreenChange?: (isFullscreen: boolean) => void
  onSubmitted?: () => void // Thông báo cho component cha khi bài đã nộp (tắt anti-cheat fullscreen)
  forceDarkMode?: boolean // Bắt buộc dark mode (cho quiz trong XemBaiHoc)
  requireFullProfile?: boolean // Yêu cầu đầy đủ thông tin (lớp, trường) mới được làm bài
}

const QuizInterface: React.FC<QuizInterfaceProps> = ({ quizData, lessonId, onBack, onFullscreenChange, onSubmitted, forceDarkMode = false, requireFullProfile = true }) => {
  const [currentQuestion, setCurrentQuestion] = useState(0)
  const [answers, setAnswers] = useState<Record<string, any>>({})
  const [timeLeft, setTimeLeft] = useState(quizData.gioi_han_thoi_gian || 900)
  const [isSubmitted, setIsSubmitted] = useState(false)
  const [showConfirmSubmit, setShowConfirmSubmit] = useState(false)
  const [startTime] = useState(Date.now())
  const [showSidebar, setShowSidebar] = useState(false)
  // Mặc định: Desktop = true (hiển thị), Mobile = false (ẩn)
  const [showQuestionList, setShowQuestionList] = useState(() => {
    if (typeof window !== 'undefined') {
      return window.innerWidth >= 1024 // lg breakpoint
    }
    return true // Default to true for SSR
  })
  const [showMenuDropdown, setShowMenuDropdown] = useState(false) // Control dropdown menu
  const [flaggedQuestions, setFlaggedQuestions] = useState<Set<string>>(new Set())
  const [showExplanation, setShowExplanation] = useState<Record<string, boolean>>({})
  // Removed local isFullscreen state, using hook instead
  const [newAchievements, setNewAchievements] = useState<Achievement[]>([])
  const [submissionResult, setSubmissionResult] = useState<{
    results: ScoringResult;
    achievements: Achievement[];
  } | null>(null)
  const [showAntiCheatWarning, setShowAntiCheatWarning] = useState(false) // Tắt modal lớn, dùng toast thay thế

  // Ref for handleSubmit to be used in useFullscreenQuiz before definition
  const handleSubmitRef = useRef<() => void>(() => { });

  // Detect nếu là bài kiểm tra GKI, CKI, GKII, CKII (từ DeTuDong)
  const isExamTest = useMemo(() => {
    return lessonId?.includes('AUTO_') ||
      quizData?.loai === 'KTGKI' ||
      quizData?.loai === 'KTCKI' ||
      quizData?.loai === 'KTGKII' ||
      quizData?.loai === 'KTCKII'
  }, [lessonId, quizData])

  // Uploading state to disable fullscreen check
  const [isUploading, setIsUploading] = useState(false)

  // Anti-cheat: Fullscreen enforcement
  const { isFullscreen, requestFullscreen, exitFullscreen, showWarning, dismissWarning, exitCount } = useFullscreenQuiz({
    enabled: isExamTest, // Chỉ bật cho bài kiểm tra
    onExitFullscreen: (count) => {
      console.log(`⚠️ [QuizInterface] Fullscreen exit detected (count: ${count})`)
      // Nếu vi phạm quá 3 lần -> Tự động nộp bài
      if (count >= 3) {
        toast.error('Bạn đã vi phạm quy chế thi quá 3 lần. Hệ thống sẽ tự động nộp bài!')
        handleSubmitRef.current()
      }
    },
    isUploading: isUploading
  })

  // Dùng cho UI: forceDarkMode (XemBaiHoc) hoặc isFullscreen state, hoặc đang upload ảnh (tránh flicker)
  const isDarkMode = forceDarkMode || isFullscreen || isUploading

  // Chuẩn hóa danh sách câu hỏi (một số nơi có thể cung cấp object thay vì array)
  const safeQuestions = useMemo<any[]>(() => {
    const raw = (quizData && (quizData.cauHoi ?? quizData.questions)) as any
    if (Array.isArray(raw)) {
      console.log(`✅ [QuizInterface] safeQuestions: ${raw.length} questions (array)`)
      return raw
    }
    if (raw && typeof raw === 'object') {
      // Nếu là object có key -> chuyển sang mảng theo thứ tự key tăng dần
      try {
        const values = Object.values(raw)
        if (Array.isArray(values)) {
          console.log(`✅ [QuizInterface] safeQuestions: ${values.length} questions (object converted)`)
          // Sort by ID to ensure stable order across re-renders
          return values.sort((a: any, b: any) => {
            const idA = a.id || a.questionId || 0;
            const idB = b.id || b.questionId || 0;
            if (typeof idA === 'string' && typeof idB === 'string') return idA.localeCompare(idB);
            return (idA as number) - (idB as number);
          })
        }
      } catch (error) {
        console.error('❌ [QuizInterface] Error converting object to array:', error)
      }
    }
    console.warn('⚠️ [QuizInterface] No questions found in quizData:', {
      hasQuizData: !!quizData,
      hasCauHoi: !!(quizData?.cauHoi),
      hasQuestions: !!(quizData?.questions)
    })
    return []
  }, [quizData])

  // Refs cho swipe gesture
  const questionContentRef = useRef<HTMLDivElement>(null)
  const questionRefs = useRef<Record<number, HTMLDivElement | null>>({})
  const touchStartX = useRef(0)
  const isSubmittingRef = useRef(false) // Prevent duplicate submit calls

  // Lấy thông tin user từ AuthContext
  const { user, checkUserInfo, loginWithGoogle } = useAuth()

  // ✅ Kiểm tra user info khi component mount
  const hasCheckedUserInfo = useRef(false)
  const [showLoginModal, setShowLoginModal] = useState(false) // ✅ Modal yêu cầu đăng nhập
  const [googleSignInFailed, setGoogleSignInFailed] = useState(false) // ✅ Google Sign-In không khả dụng (mobile/Zalo)
  const [chromeCountdown, setChromeCountdown] = useState(3)
  const [linkCopied, setLinkCopied] = useState(false)

  useEffect(() => {
    const checkUser = async () => {
      // ✅ Bước 1: Yêu cầu đăng nhập với Google nếu chưa đăng nhập
      if (!user) {
        console.log('⚠️ [QuizInterface] User not logged in, showing login modal')
        setShowLoginModal(true) // Hiện modal đăng nhập
        return
      }

      // Chỉ kiểm tra một lần khi mount và khi user đã có
      if (hasCheckedUserInfo.current || isSubmitted) return

      // ✅ Bước 2: Kiểm tra thông tin đầy đủ nếu có yêu cầu
      if (requireFullProfile) {
        const needsInfo = await checkUserInfo()
  
        if (needsInfo) {
          console.log('⚠️ [QuizInterface] User needs info or not in database, going back to show registration modal')
          // Quay lại để hiện modal đăng ký/bổ sung thông tin
          onBack()
        } else {
          console.log('✅ [QuizInterface] User has complete info, can proceed')
        }
      }
      
      hasCheckedUserInfo.current = true
    }

    // Chỉ kiểm tra khi chưa submit
    if (!isSubmitted) {
      checkUser()
    }

    // Reset khi component unmount
    return () => {
      hasCheckedUserInfo.current = false
    }
  }, [user, checkUserInfo, isSubmitted, onBack, requireFullProfile])

  // ✅ Ref cho Google Sign-In button container
  const googleButtonRef = useRef<HTMLDivElement>(null);
  // ✅ Track việc initialize Google OAuth để tránh initialize lại
  const isGoogleOAuthInitialized = useRef(false);
  // ✅ Track timeout để cleanup
  const initTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // ✅ Đảm bảo modal đăng nhập luôn hiện nếu chưa đăng nhập
  useEffect(() => {
    if (!user && !showLoginModal && !isSubmitted) {
      console.log('⚠️ [QuizInterface] Ensuring login modal is shown');
      setShowLoginModal(true);
    }
  }, [user, showLoginModal, isSubmitted]);

  // ✅ Xử lý Google Sign-In khi modal đăng nhập được hiện
  useEffect(() => {
    // Cleanup timeout khi unmount hoặc modal đóng
    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, []);

  // Auto-open Chrome khi Google Sign-In thất bại (Zalo/in-app browser)
  useEffect(() => {
    if (!googleSignInFailed) { setChromeCountdown(3); return; }
    if (chromeCountdown <= 0) {
      const url = window.location.href;
      const ua = navigator.userAgent;
      if (/Android/i.test(ua)) {
        const fallback = encodeURIComponent('https://play.google.com/store/apps/details?id=com.android.chrome');
        window.location.href = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
      } else if (/iPhone|iPad|iPod/i.test(ua)) {
        window.location.href = url.replace(/^https?/, 'googlechrome');
      }
      return;
    }
    const t = setTimeout(() => setChromeCountdown(c => c - 1), 1000);
    return () => clearTimeout(t);
  }, [googleSignInFailed, chromeCountdown]);

  useEffect(() => {
    if (!showLoginModal) {
      // Reset flag khi modal đóng để có thể initialize lại khi mở lại
      isGoogleOAuthInitialized.current = false;
      setGoogleSignInFailed(false);
      setChromeCountdown(3);
      setLinkCopied(false);
      return;
    }

    // ✅ Đảm bảo container tồn tại trước khi bắt đầu
    // Sử dụng requestAnimationFrame để đảm bảo DOM đã render
    const checkContainer = () => {
      if (!googleButtonRef.current) {
        console.warn('⚠️ [QuizInterface] Button container not found, retrying...');
        // Retry sau 100ms
        setTimeout(checkContainer, 100);
        return;
      }

      console.log('🔍 [QuizInterface] Container found, starting initialization...');

      // Initialize Google OAuth first, then render button
      const initializeAndRender = async () => {
        // ✅ DEBUG: Log environment và state
        const clientId = import.meta.env.VITE_GOOGLE_CLIENT_ID;
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');
        console.log('🔍 [QuizInterface] DEBUG - Starting Google Sign-In initialization');
        console.log('📊 Environment check:', {
          hasClientId: !!clientId,
          clientIdLength: clientId?.length || 0,
          clientIdPreview: clientId ? `${clientId.substring(0, 20)}...` : 'MISSING',
          isTestClientId: clientId === 'test-client-id',
          hasGoogle: !!window.google,
          hasGoogleAccounts: !!(window.google && window.google.accounts),
          hasGoogleAccountsId: !!(window.google && window.google.accounts && window.google.accounts.id),
          containerExists: !!googleButtonRef.current,
          modalVisible: showLoginModal
        });
        console.log('━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━━');

        // Kiểm tra lại container trước khi tiếp tục
        if (!googleButtonRef.current) {
          console.error('❌ [QuizInterface] Container lost during initialization');
          return;
        }

        // Kiểm tra client_id
        if (!clientId || clientId === 'test-client-id') {
          console.error('❌ [QuizInterface] VITE_GOOGLE_CLIENT_ID is missing or invalid!');
          console.error('📊 Client ID value:', clientId);
          if (googleButtonRef.current) {
            googleButtonRef.current.innerHTML = `
              <div class="text-center p-4">
                <p class="text-sm text-red-500 mb-2">Lỗi cấu hình: Google Client ID chưa được thiết lập</p>
                <p class="text-xs text-gray-500">Vui lòng liên hệ quản trị viên</p>
              </div>
            `;
          }
          return;
        }

        // Luôn clear để đảm bảo button được render mới
        googleButtonRef.current.innerHTML = '';

        try {
          console.log('🔄 [QuizInterface] Initializing Google Sign-In...');

          // First, load Google OAuth script if not already loaded
          if (!window.google || !window.google.accounts || !window.google.accounts.id) {
            console.log('📥 [QuizInterface] Loading Google OAuth script...');
            const script = document.createElement('script');
            script.src = 'https://accounts.google.com/gsi/client';
            script.async = true;
            script.defer = true;
            script.id = 'google-oauth-script';

            // Remove existing script if any
            const existingScript = document.getElementById('google-oauth-script');
            if (existingScript) {
              existingScript.remove();
            }

            await new Promise((resolve, reject) => {
              const timeout = setTimeout(() => {
                console.error('❌ [QuizInterface] Script load timeout after 10 seconds');
                reject(new Error('Script load timeout'));
              }, 10000);

              script.onload = () => {
                clearTimeout(timeout);
                console.log('✅ [QuizInterface] Google OAuth script loaded');
                // Wait a bit more for Google API to be ready
                setTimeout(() => {
                  if (window.google && window.google.accounts && window.google.accounts.id) {
                    console.log('✅ [QuizInterface] Google OAuth API is ready');
                    resolve(null);
                  } else {
                    console.warn('⚠️ [QuizInterface] Script loaded but API not ready yet');
                    resolve(null); // Continue anyway
                  }
                }, 500);
              };
              script.onerror = (error) => {
                clearTimeout(timeout);
                console.error('❌ [QuizInterface] Failed to load Google OAuth script:', error);
                console.error('📊 Script src:', script.src);
                console.error('📊 Network error details:', {
                  message: error instanceof Error ? error.message : 'Unknown error',
                  type: error instanceof Error ? error.name : typeof error
                });
                reject(error);
              };
              document.head.appendChild(script);
            });
          } else {
            console.log('✅ [QuizInterface] Google OAuth script already loaded');
          }

          // Wait a bit for Google OAuth to be ready
          let retries = 0;
          const maxRetries = 10;
          while (retries < maxRetries && (!window.google || !window.google.accounts || !window.google.accounts.id)) {
            console.log(`⏳ [QuizInterface] Waiting for Google OAuth API (attempt ${retries + 1}/${maxRetries})...`);
            await new Promise(resolve => setTimeout(resolve, 200));
            retries++;
          }

          // Kiểm tra lại container sau khi đợi
          if (!googleButtonRef.current) {
            console.error('❌ [QuizInterface] Container lost after waiting');
            return;
          }

          // Now check if Google OAuth is available
          if (window.google && window.google.accounts && window.google.accounts.id) {
            console.log('✅ [QuizInterface] Google OAuth available, initializing...');
            console.log('📊 Client ID being used:', clientId.substring(0, 30) + '...');

            // Chỉ initialize một lần
            if (!isGoogleOAuthInitialized.current) {
              try {
                window.google.accounts.id.initialize({
                  client_id: clientId,
                  callback: async (response: any) => {
                    console.log('✅ [QuizInterface] Google OAuth callback received');
                    try {
                      // Đăng nhập với Google
                      await loginWithGoogle(response);
                      console.log('✅ [QuizInterface] Login successful');

                      // Đóng modal đăng nhập
                      setShowLoginModal(false);

                      // ✅ Sau khi đăng nhập thành công, kiểm tra thông tin và hiện popup bổ sung nếu cần
                      setTimeout(async () => {
                        const needsInfo = await checkUserInfo();
                        if (needsInfo) {
                          console.log('⚠️ [QuizInterface] User needs info after login, going back to show registration modal');
                          onBack(); // Quay lại để hiện modal đăng ký
                        }
                      }, 500);
                    } catch (error) {
                      console.error('❌ [QuizInterface] Error during login:', error);
                      toast.error('Đăng nhập thất bại. Vui lòng thử lại.');
                    }
                  },
                  auto_select: false,
                  cancel_on_tap_outside: true,
                });
                isGoogleOAuthInitialized.current = true;
                console.log('✅ [QuizInterface] Google OAuth initialized successfully');
              } catch (initError) {
                console.error('❌ [QuizInterface] Error during Google OAuth initialize:', initError);
                if (googleButtonRef.current) {
                  googleButtonRef.current.innerHTML = `
                    <div class="text-center p-4">
                      <p class="text-sm text-red-500 mb-2">Lỗi khởi tạo Google Sign-In</p>
                      <p class="text-xs text-gray-500">${initError instanceof Error ? initError.message : 'Unknown error'}</p>
                    </div>
                  `;
                }
                return;
              }
            } else {
              console.log('ℹ️ [QuizInterface] Google OAuth already initialized');
            }

            // LUÔN render button
            if (googleButtonRef.current) {
              console.log('🎨 [QuizInterface] Rendering Google Sign-In button...');
              console.log('📊 Container element:', {
                exists: !!googleButtonRef.current,
                id: googleButtonRef.current.id,
                className: googleButtonRef.current.className,
                innerHTML: googleButtonRef.current.innerHTML.substring(0, 50)
              });

              try {
                window.google.accounts.id.renderButton(googleButtonRef.current, {
                  theme: 'outline',
                  size: 'large',
                  width: 300,
                  text: 'signin_with',
                  shape: 'rectangular'
                });
                console.log('✅ [QuizInterface] Google Sign-In button render call completed');

                // Kiểm tra lại sau 1 giây xem button đã xuất hiện chưa
                setTimeout(() => {
                  if (googleButtonRef.current) {
                    const buttonNow = googleButtonRef.current.querySelector('div[role="button"]') ||
                      googleButtonRef.current.querySelector('button') ||
                      googleButtonRef.current.querySelector('iframe');
                    if (buttonNow) {
                      console.log('✅ [QuizInterface] Google Sign-In button confirmed visible');
                      console.log('📊 Button element:', {
                        tagName: buttonNow.tagName,
                        role: buttonNow.getAttribute('role'),
                        hasContent: buttonNow.innerHTML.length > 0
                      });
                    } else {
                      console.warn('⚠️ [QuizInterface] Google Sign-In button not found after render');
                      console.warn('📊 Container state:', {
                        innerHTML: googleButtonRef.current.innerHTML.substring(0, 200),
                        childrenCount: googleButtonRef.current.children.length,
                        hasIframe: !!googleButtonRef.current.querySelector('iframe')
                      });
                      // Retry render
                      console.log('🔄 [QuizInterface] Retrying button render...');
                      googleButtonRef.current.innerHTML = '';
                      try {
                        window.google.accounts.id.renderButton(googleButtonRef.current, {
                          theme: 'outline',
                          size: 'large',
                          width: 300,
                          text: 'signin_with',
                          shape: 'rectangular'
                        });
                        console.log('✅ [QuizInterface] Retry render completed');
                      } catch (retryError) {
                        console.error('❌ [QuizInterface] Retry render failed:', retryError);
                      }
                    }
                  }
                }, 1000);
              } catch (renderError) {
                console.error('❌ [QuizInterface] Error rendering button:', renderError);
                console.error('📊 Error details:', {
                  message: renderError instanceof Error ? renderError.message : 'Unknown error',
                  name: renderError instanceof Error ? renderError.name : typeof renderError,
                  stack: renderError instanceof Error ? renderError.stack : undefined
                });
                setGoogleSignInFailed(true);
              }
            } else {
              console.error('❌ [QuizInterface] Container is null when trying to render button');
            }
          } else {
            console.error('❌ [QuizInterface] Google OAuth not available after wait');
            console.error('📊 Final state:', {
              hasGoogle: !!window.google,
              hasAccounts: !!(window.google && window.google.accounts),
              hasAccountsId: !!(window.google && window.google.accounts && window.google.accounts.id)
            });
            setGoogleSignInFailed(true);
          }
        } catch (error) {
          console.error('❌ [QuizInterface] Error initializing Google Sign-In:', error);
          console.error('📊 Error details:', {
            message: error instanceof Error ? error.message : 'Unknown error',
            name: error instanceof Error ? error.name : typeof error,
            stack: error instanceof Error ? error.stack : undefined
          });
          setGoogleSignInFailed(true);
        }
      };

      // Start initialization and rendering
      initializeAndRender();
    };

    // ✅ Sử dụng requestAnimationFrame để đảm bảo DOM đã render
    requestAnimationFrame(() => {
      // Thêm delay nhỏ để đảm bảo modal đã mount hoàn toàn
      setTimeout(checkContainer, 100);
    });

    return () => {
      if (initTimeoutRef.current) {
        clearTimeout(initTimeoutRef.current);
        initTimeoutRef.current = null;
      }
    };
  }, [showLoginModal, loginWithGoogle, checkUserInfo, onBack]); // Thêm dependencies để đảm bảo callback luôn mới nhất

  // ✅ PHASE 2: Quiz Tracking Hook
  const quizId = lessonId
  const {
    saveQuizResult
  } = useQuizTracking({
    quizId,
    lessonId,
    quizTitle: quizData.tieuDe || 'Trắc nghiệm',
    quizType: 'lesson',
    timeLimitSeconds: quizData.gioi_han_thoi_gian || 900
  })

  // Removed old isUploading and useFullscreenQuiz code

  // Debug: Log isUploading state changes
  useEffect(() => {
    console.log('🔄 [QuizInterface] isUploading changed to:', isUploading);
  }, [isUploading]);

  // Detect if running on mobile/tablet device
  const isMobileDevice = () => {
    return /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent)
  }

  // Detect actual screen size for proper fullscreen layout
  const isSmallScreen = () => {
    return window.innerWidth < 1024 // lg breakpoint
  }

  // Timer cho thời gian còn lại
  useEffect(() => {
    if (!isSubmitted) {
      const timer = setInterval(() => {
        setTimeLeft(prev => {
          if (prev <= 1) {
            handleSubmit()
            return 0
          }
          return prev - 1
        })
      }, 1000)
      return () => clearInterval(timer)
    }
  }, [isSubmitted])

  // Đóng dropdown menu khi nhấn ESC
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && showMenuDropdown) {
        setShowMenuDropdown(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [showMenuDropdown])

  // Update showQuestionList khi resize window (để sync với screen size)
  useEffect(() => {
    const handleResize = () => {
      const isDesktop = window.innerWidth >= 1024 // lg breakpoint
      // Chỉ update nếu chưa được user thay đổi thủ công (optional - có thể bỏ nếu muốn user control hoàn toàn)
      // setShowQuestionList(isDesktop)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])


  // Cập nhật số câu trong cluster context theo displayNumber thực tế (cùng logic tronde DocxGeneratorService)
  const rebuildClusterCtx = (ctx: string, firstNum: number, lastNum: number): string => {
    if (!ctx) return '';
    // Thay "cho Câu 10 và Câu 11" → "cho Câu 6 và Câu 7" theo số hiển thị thực tế
    return ctx.replace(
      /(cho\s*(?:câu|Câu)?\s*)(\d+)(\s*(?:và|đến|-)\s*(?:câu|Câu)?\s*)(\d+)/i,
      `$1${firstNum}$3${lastNum}`
    );
  };

  // Lấy firstNum và lastNum của cluster từ displayNumberMap
  const getClusterNums = (clusterId: string, questions: any[]): { first: number; last: number } => {
    const clusterQs = questions
      .map((q: any, idx: number) => ({ q, idx }))
      .filter(({ q }) => q.examMetadata?.cluster_id === clusterId);
    if (!clusterQs.length) return { first: 1, last: 1 };
    const first = displayNumberMap[clusterQs[0].idx] ?? (clusterQs[0].idx + 1);
    const last  = displayNumberMap[clusterQs[clusterQs.length - 1].idx] ?? (clusterQs[clusterQs.length - 1].idx + 1);
    return { first, last };
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, '0')}`
  }



  // Tên học sinh từ thông tin tài khoản
  const getStudentName = () => {
    if (user?.name) {
      return user.name
    }
    return 'Học sinh'
  }

  // Get progress for True/False questions
  const getTrueFalseProgress = (questionId: string) => {
    const questionAnswers = answers[questionId]
    if (!questionAnswers || typeof questionAnswers !== 'object') return 0

    // Count how many statements have been answered
    return Object.keys(questionAnswers).length
  }

  const resolveQuestionGroup = (question: any): QuestionGroup => {
    if (!question) return 'OTHER'
    const metaGroup = question.examMetadata?.group
    if (metaGroup && (metaGroup === 'TN' || metaGroup === 'DS' || metaGroup === 'TL' || metaGroup === 'TLU')) {
      return metaGroup
    }
    const fallback = GROUP_FALLBACK_BY_TYPE[question.type as string]
    return fallback || 'OTHER'
  }

  const questionSections = useMemo(() => {
    const sections: Record<QuestionGroup, number[]> = {
      TN: [],
      DS: [],
      TL: [],
      TLU: [],
      OTHER: []
    }
    for (let index = 0; index < safeQuestions.length; index++) {
      const question = safeQuestions[index]
      const group = resolveQuestionGroup(question)
      sections[group].push(index)
    }
    // Sắp xếp tăng dần theo chỉ số câu hỏi trong từng phần
    (Object.keys(sections) as QuestionGroup[]).forEach(g => {
      sections[g].sort((a, b) => a - b)
    })
    const order: QuestionGroup[] = ['TN', 'DS', 'TL', 'TLU', 'OTHER']
    const sectionList = order
      .filter(group => sections[group].length > 0)
      .map(group => ({
        group,
        title: GROUP_TITLES[group],
        indices: sections[group]
      }))
    if (sectionList.length === 0 && safeQuestions.length) {
      return [
        {
          group: 'OTHER' as QuestionGroup,
          title: GROUP_TITLES.OTHER,
          indices: safeQuestions.map((_: any, index: number) => index)
        }
      ]
    }
    return sectionList
  }, [safeQuestions])

  // Scroll to question when clicking dot (when showing all questions)
  const scrollToQuestion = (index: number) => {
    setCurrentQuestion(index)
    if (showQuestionList && questionRefs.current[index]) {
      questionRefs.current[index]?.scrollIntoView({
        behavior: 'smooth',
        block: 'start',
        inline: 'nearest'
      })
    }
  }

  const renderQuestionDot = (index: number, variant: 'mobile' | 'desktop' = 'desktop', displayNumber?: number) => {
    const question = safeQuestions?.[index]
    if (!question) return null
    const isAnswered = isQuestionAnswered(question.id)
    const isFlagged = flaggedQuestions.has(question.id)
    const isCurrent = index === currentQuestion

    if (variant === 'mobile') {
      return (
        <button
          key={question.id}
          onClick={() => scrollToQuestion(index)}
          className="quiz-dot-mobile no-select"
          style={{
            background: isCurrent ? '#2563eb' : isAnswered ? '#10b981' : 'white',
            color: isCurrent || isAnswered ? 'white' : '#0f172a',
            border: isCurrent ? '2px solid #1d4ed8' : isAnswered ? '2px solid #059669' : '2px solid #94a3b8',
            width: '28px',
            height: '28px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: '11px',
            fontWeight: 'bold',
            flexShrink: 0
          }}
          title={`Câu ${displayNumber ?? (index + 1)}${isAnswered ? ' - Đã trả lời' : ''}${isFlagged ? ' - Đã đánh dấu' : ''}`}
          aria-label={`Câu hỏi ${displayNumber ?? (index + 1)}`}
        >
          {displayNumber ?? (index + 1)}
        </button>
      )
    }

    return (
      <button
        key={question.id}
        onClick={() => scrollToQuestion(index)}
        className={`quiz-dot no-select ${isCurrent ? 'current' :
          isAnswered ? 'answered' :
            'unanswered'
          } ${isFlagged ? 'flagged' : ''} ${!isAnswered && !isCurrent ? 'border-2 border-dashed border-orange-400' : ''}`}
        style={{
          background: isCurrent ? '#3b82f6' : isAnswered ? '#10b981' : 'white',
          color: isCurrent || isAnswered ? 'white' : '#64748b',
          border: isCurrent ? '3px solid #2563eb' : isAnswered ? '2px solid #059669' : '2px solid #94a3b8'
        }}
        title={`Câu ${displayNumber ?? (index + 1)}${isAnswered ? ' - Đã trả lời' : ''}${isFlagged ? ' - Đã đánh dấu' : ''}`}
        aria-label={`Câu hỏi ${displayNumber ?? (index + 1)}`}
      >
        {displayNumber ?? (index + 1)}
      </button>
    )
  }

  // Danh sách chỉ số câu hỏi đã sort tăng dần
  const sortedIndices = useMemo(() => {
    return Array.from({ length: safeQuestions.length }, (_, i) => i)
  }, [safeQuestions.length])

  // Nhóm câu hỏi theo LOẠI: TN → DS → TL (bắt buộc các câu cùng loại nằm cùng nhóm)
  const groupedSections = useMemo(() => {
    const byGroup: Record<QuestionGroup, number[]> = { TN: [], DS: [], TL: [], TLU: [], OTHER: [] }
    for (let i = 0; i < safeQuestions.length; i++) {
      const q = safeQuestions[i]
      const g = resolveQuestionGroup(q)
      byGroup[g].push(i)
    }
    ; (Object.keys(byGroup) as QuestionGroup[]).forEach(g => byGroup[g].sort((a, b) => a - b))
    const result: Array<{ title: string; indices: number[] }> = []
    if (byGroup.TN.length) result.push({ title: 'Phần I - Trắc nghiệm', indices: byGroup.TN })
    if (byGroup.DS.length) result.push({ title: 'Phần II - Đúng/Sai', indices: byGroup.DS })
    if (byGroup.TL.length) result.push({ title: 'Phần III - Trả lời ngắn', indices: byGroup.TL })
    if (byGroup.TLU.length) result.push({ title: 'Phần IV - Tự luận', indices: byGroup.TLU })
    if (byGroup.OTHER.length) result.push({ title: 'Các câu hỏi khác', indices: byGroup.OTHER })

    // Fallback: Nếu không có section nào (có thể do lỗi phân loại), hiển thị tất cả câu hỏi
    if (result.length === 0 && safeQuestions.length > 0) {
      console.warn('⚠️ [QuizInterface] No sections found, using fallback: all questions in one section')
      result.push({
        title: 'Tất cả câu hỏi',
        indices: Array.from({ length: safeQuestions.length }, (_, i) => i)
      })
    }

    return result
  }, [safeQuestions])

  // Mobile summary counts: P1 1-n, P2 1-n, P3 1-n (one-line each)
  const mobileGroupCounts = useMemo(() => {
    let tn = 0, ds = 0, tl = 0, tlu = 0
    for (let i = 0; i < safeQuestions.length; i++) {
      const g = resolveQuestionGroup(safeQuestions[i])
      if (g === 'TN') tn++
      else if (g === 'DS') ds++
      else if (g === 'TL') tl++
      else if (g === 'TLU') tlu++
    }
    return { tn, ds, tl, tlu }
  }, [safeQuestions])

  // Bản đồ số thứ tự hiển thị liên tục 1..N theo thứ tự nhóm (TN → DS → TL → OTHER)
  const displayNumberMap = useMemo(() => {
    const order: number[] = []
    groupedSections.forEach(section => {
      order.push(...section.indices)
    })
    const map: Record<number, number> = {}
    order.forEach((idx, i) => { map[idx] = i + 1 })
    // Debug log for exam tests
    if (isExamTest && Object.keys(map).length > 0) {
      console.log('📊 [QuizInterface] displayNumberMap:', map)
      console.log('📊 [QuizInterface] groupedSections:', groupedSections.map(s => ({ title: s.title, count: s.indices.length })))
    }
    return map
  }, [groupedSections, isExamTest])

  // Reverse map: từ displayNumber (1, 2, 3...) về index trong mảng
  const indexFromDisplayNumber = useMemo(() => {
    const reverseMap: Record<number, number> = {}
    Object.entries(displayNumberMap).forEach(([index, displayNum]) => {
      reverseMap[displayNum] = parseInt(index)
    })
    return reverseMap
  }, [displayNumberMap])

  // Hàm chuyển câu hỏi theo thứ tự displayNumber liên tục
  const goToNextQuestion = useCallback(() => {
    setCurrentQuestion(prev => {
      const currentDisplayNum = displayNumberMap[prev] ?? (prev + 1)
      const nextDisplayNum = currentDisplayNum + 1
      const nextIndex = indexFromDisplayNumber[nextDisplayNum]
      if (nextIndex !== undefined) {
        return nextIndex
      }
      // Fallback: nếu không tìm thấy, tăng index
      return Math.min((safeQuestions.length || 1) - 1, prev + 1)
    })
  }, [safeQuestions.length, displayNumberMap, indexFromDisplayNumber])

  const goToPreviousQuestion = useCallback(() => {
    setCurrentQuestion(prev => {
      const currentDisplayNum = displayNumberMap[prev] ?? (prev + 1)
      const prevDisplayNum = currentDisplayNum - 1
      const prevIndex = indexFromDisplayNumber[prevDisplayNum]
      if (prevIndex !== undefined) {
        return prevIndex
      }
      // Fallback: nếu không tìm thấy, giảm index
      return Math.max(0, prev - 1)
    })
  }, [displayNumberMap, indexFromDisplayNumber])

  // Fullscreen functions
  const toggleFullscreen = async () => {
    if (!isFullscreen) {
      await requestFullscreen()
    } else {
      await exitFullscreen()
    }
  }

  // Sync fullscreen state with parent (maintain dark mode in parent)
  useEffect(() => {
    // ✅ QUAN TRỌNG: Không gọi onFullscreenChange khi đã submit (đang hiện phân tích)
    // Tránh XemBaiHoc unmount component khi thoát fullscreen để hiện kết quả
    if (!isSubmitted) {
      onFullscreenChange?.(isFullscreen)
    }
  }, [isFullscreen, isSubmitted, onFullscreenChange])

  // Swipe gesture cho mobile
  useEffect(() => {
    const contentElement = questionContentRef.current
    if (!contentElement || !isMobileDevice()) return // Chỉ kích hoạt trên mobile

    const handleTouchStart = (e: TouchEvent) => {
      touchStartX.current = e.touches[0].clientX
    }

    const handleTouchEnd = (e: TouchEvent) => {
      const touchEndX = e.changedTouches[0].clientX
      const swipeDistance = touchEndX - touchStartX.current
      const minSwipeDistance = 50 // Khoảng cách vuốt tối thiểu để kích hoạt

      console.log('Swipe detected:', {
        startX: touchStartX.current,
        endX: touchEndX,
        distance: swipeDistance,
        minDistance: minSwipeDistance
      })

      if (swipeDistance > minSwipeDistance) {
        // Vuốt sang phải = chuyển về câu trước (như lướt về trước)
        console.log('Swipe right - Previous question')
        goToPreviousQuestion()
      } else if (swipeDistance < -minSwipeDistance) {
        // Vuốt sang trái = chuyển sang câu sau (như lướt tiếp)
        console.log('Swipe left - Next question')
        goToNextQuestion()
      }
    }

    contentElement.addEventListener('touchstart', handleTouchStart)
    contentElement.addEventListener('touchend', handleTouchEnd)

    return () => {
      contentElement.removeEventListener('touchstart', handleTouchStart)
      contentElement.removeEventListener('touchend', handleTouchEnd)
    }
  }, [currentQuestion, safeQuestions.length, goToNextQuestion, goToPreviousQuestion])

  // Keyboard và Mouse wheel navigation cho Desktop
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Chỉ kích hoạt trên Desktop và khi không đang focus vào input/textarea
      if (isMobileDevice()) return

      const activeElement = document.activeElement
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement).contentEditable === 'true'
      )

      if (isInputFocused) return // Không xử lý khi đang nhập liệu

      switch (e.key) {
        case 'ArrowLeft':
        case 'ArrowUp':
          e.preventDefault()
          goToPreviousQuestion()
          break
        case 'ArrowRight':
        case 'ArrowDown':
          e.preventDefault()
          goToNextQuestion()
          break
      }
    }

    const handleWheel = (e: WheelEvent) => {
      // Chỉ kích hoạt trên Desktop và khi không đang focus vào input/textarea
      if (isMobileDevice()) return

      const activeElement = document.activeElement
      const isInputFocused = activeElement && (
        activeElement.tagName === 'INPUT' ||
        activeElement.tagName === 'TEXTAREA' ||
        (activeElement as HTMLElement).contentEditable === 'true'
      )

      if (isInputFocused) return // Không xử lý khi đang nhập liệu

      // Kiểm tra nếu đang scroll trong vùng quiz (rộng hơn vùng câu hỏi)
      const questionContent = questionContentRef.current
      if (!questionContent) return

      // Mở rộng vùng hoạt động để bao gồm cả header và navigation
      const rect = questionContent.getBoundingClientRect()
      const expandedRect = {
        left: rect.left - 50,
        right: rect.right + 50,
        top: rect.top - 100,
        bottom: rect.bottom + 100
      }

      const isInQuizArea = e.clientX >= expandedRect.left && e.clientX <= expandedRect.right &&
        e.clientY >= expandedRect.top && e.clientY <= expandedRect.bottom

      console.log('Wheel event:', {
        clientX: e.clientX,
        clientY: e.clientY,
        deltaY: e.deltaY,
        originalRect: { left: rect.left, right: rect.right, top: rect.top, bottom: rect.bottom },
        expandedRect,
        isInQuizArea,
        isInputFocused
      })

      if (isInQuizArea) {
        e.preventDefault()

        if (e.deltaY > 0) {
          // Scroll xuống = chuyển sang câu sau
          console.log('Wheel down - Next question')
          goToNextQuestion()
        } else if (e.deltaY < 0) {
          // Scroll lên = chuyển về câu trước
          console.log('Wheel up - Previous question')
          goToPreviousQuestion()
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown)
    document.addEventListener('wheel', handleWheel, { passive: false })

    return () => {
      document.removeEventListener('keydown', handleKeyDown)
      document.removeEventListener('wheel', handleWheel)
    }
  }, []) // Empty dependency array để tránh re-create

  const handleAnswerChange = (questionId: string, answer: any) => {
    console.log('Answer changed:', { questionId, answer, type: typeof answer })
    setAnswers(prev => ({
      ...prev,
      [questionId]: answer
    }))
  }

  const getAnsweredCount = () => {
    return Object.keys(answers).filter(key => {
      const answer = answers[key]
      if (answer === null || answer === undefined || answer === '') return false

      // Handle multi-statement True/False questions
      if (typeof answer === 'object' && !Array.isArray(answer)) {
        // ✅ TLU Check: If it has aiGrade, it's answered
        if (answer.aiGrade) return true;

        // Check if at least some statements are answered
        const statementAnswers = Object.values(answer)
        const answeredStatements = statementAnswers.filter(a => a !== null && a !== undefined).length
        return answeredStatements >= Math.ceil(statementAnswers.length * 0.75)
      }

      return true
    }).length
  }

  const isQuestionAnswered = (questionId: string) => {
    const answer = answers[questionId]
    if (answer === null || answer === undefined || answer === '') return false

    // Handle multi-statement True/False questions
    if (typeof answer === 'object' && !Array.isArray(answer)) {
      // ✅ TLU Check: If it has aiGrade, it's answered
      if (answer.aiGrade) return true;

      // Check if at least some statements are answered
      const statementAnswers = Object.values(answer)
      return statementAnswers.length > 0 && statementAnswers.some(a => a !== null && a !== undefined)
    }

    return true
  }

  const handleSubmit = async () => {
    // ✅ Prevent duplicate submit calls
    if (isSubmittingRef.current) {
      console.log('⚠️ [QuizInterface] handleSubmit already in progress, ignoring');
      return;
    }

    isSubmittingRef.current = true

    // ✅ QUAN TRỌNG: Set isSubmitted = true TRƯỚC để disable useFullscreenQuiz
    // Tránh loop: exitFullscreen → fullscreenchange → onExitFullscreen → handleSubmit()
    setIsSubmitted(true)
    setShowConfirmSubmit(false)
    onSubmitted?.()  // Báo component cha: đã nộp → tắt cảnh báo fullscreen

    // ✅ FORCE EXIT BROWSER FULLSCREEN sau khi đã disable tracking
    // Để QuizSubmission luôn hiện với thanh trình duyệt (Full chế độ 2)
    if (document.fullscreenElement) {
      try {
        await document.exitFullscreen()
        console.log('🖥️ Exited browser fullscreen before showing results')
      } catch (err) {
        console.warn('Failed to exit fullscreen:', err)
      }
    }

    toast.success('Đang nộp bài...')

    // ✅ PHASE 2: Calculate score và save quiz result
    try {
      // 0. Handle Deferred AI Grading for TLU questions
      let finalAnswers = { ...answers };
      const pendingQuestions = Object.entries(finalAnswers).filter(([qId, ans]) =>
        ans && typeof ans === 'object' && ans.aiGrade && ans.aiGrade.totalScore === -1
      );

      if (pendingQuestions.length > 0) {
        console.log(`🤖 [QuizInterface] Found ${pendingQuestions.length} pending TLU questions. Grading now...`);
        toast.loading('Đang chấm điểm tự luận bằng AI...', { id: 'grading-toast' });

        // Process sequentially to avoid rate limits or overwhelming the client
        for (const [qId, ans] of pendingQuestions) {
          try {
            const question = safeQuestions.find(q => q.id === qId);
            if (!question) continue;

            const gradeRes = await autoCoringService.gradeSubmission(
              ans.aiGrade.parts,
              question.cau_hoi || question.cauHoi || '', // Question content
              question.dap_an_dung || question.loi_giai || '', // Correct answer
              { penalizeMissingUnit: false } // Default options
            );

            // Update answer with real grade
            finalAnswers[qId] = {
              ...ans,
              aiGrade: gradeRes,
              userAnswer: gradeRes.parts.map(p => p.content).join('\n') // Ensure text is set
            };

            console.log(`✅ [QuizInterface] Graded question ${qId}: ${gradeRes.totalScore}/${gradeRes.totalMaxScore}`);
          } catch (err) {
            console.error(`❌ [QuizInterface] Failed to grade question ${qId}:`, err);
            // Keep pending status or mark as error?
            // For now, keep as is, it will be scored as 0 by QuizScoringService if not handled
          }
        }

        toast.dismiss('grading-toast');
        toast.success('Đã chấm xong tự luận!');

        // Update state to reflect grades in UI
        setAnswers(finalAnswers);
      }

      const timeSpent = Math.floor((Date.now() - startTime) / 1000)

      // 1. Chấm điểm (Use finalAnswers)
      console.log('🎯 [QuizInterface] Scoring quiz...')
      const scoringResult = QuizScoringService.scoreQuiz(
        safeQuestions,
        finalAnswers,
        timeSpent,
        quizData?.diem_dat || quizData?.metadata?.diemDat || 70
      )

      let newAchievements: Achievement[] = []

      // Gửi bài làm lên PMaker Backend nếu là đề PMaker
      if (quizData?.settings && user?.id) {
        try {
          console.log('🌐 [QuizInterface] Submitting to PMaker backend...')
          const token = localStorage.getItem('jwt_token')
          const pmakerRes = await fetch(`${API_CONFIG.python.apiUrl}/pmaker-advanced/submit/${quizData.id}`, {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json',
              'Authorization': `Bearer ${token}`
            },
            body: JSON.stringify({
              answers: finalAnswers,
              duration_seconds: timeSpent
            })
          })

          if (pmakerRes.ok) {
            const pmakerData = await pmakerRes.json()
            if (pmakerData.success) {
              console.log('✅ PMaker submit success', pmakerData.data)
              // Update local score from backend
              let backendScore = (pmakerData.data.score / pmakerData.data.max_score) * 10
              if (isNaN(backendScore)) backendScore = 0
              scoringResult.score = backendScore
            }
          } else {
            console.error('❌ Lỗi nộp bài PMaker:', await pmakerRes.text())
            const errorData = await pmakerRes.json().catch(()=>({}));
            if(errorData.detail?.includes('hết lượt')) {
               toast.error('Bạn đã hết lượt làm bài!');
            }
          }
        } catch (error) {
          console.error('Failed to submit PMaker exam:', error)
        }
      }

      // 2. Lưu kết quả (nếu đã đăng nhập)
      if (user?.id) {
        console.log('💾 [QuizInterface] Saving result...')
        const result = await saveQuizResult(
          scoringResult.score,
          scoringResult.answers.length,
          scoringResult.answers.filter(a => a.isCorrect).length,
          scoringResult.answers.filter(a => !a.isCorrect).length,
          0, // skippedAnswers
          scoringResult.answers.map(a => ({
            questionId: a.questionId,
            userAnswer: JSON.stringify(a.userAnswer),
            correctAnswer: JSON.stringify(a.correctAnswer),
            isCorrect: a.isCorrect,
            timeSpent: a.timeSpent,
            questionText: '', // Optional
            questionType: a.questionType as any
          })),
          undefined, // metadata
          exitCount // fullscreenExitCount for anti-cheat
        )

        if (result?.achievements && result.achievements.length > 0) {
          newAchievements = result.achievements
          setNewAchievements(result.achievements)
          console.log('🏆 Achievements unlocked:', result.achievements)
        }

        // 3. Gửi email
        console.log('📧 [QuizInterface] Sending email...')
        try {
          await emailServicePHP.notifyLessonCompletion(
            user.name || 'Học viên',
            user.email,
            quizData.tieuDe || quizData.ten || 'Bài kiểm tra',
            lessonId,
            scoringResult.answers.filter(a => a.isCorrect).length,
            scoringResult.answers.length,
            timeSpent
          )
        } catch (error) {
          console.error('❌ [QuizInterface] Failed to send email:', error)
        }
      }

      // 4. Set result để hiển thị QuizSubmission
      setSubmissionResult({
        results: scoringResult,
        achievements: newAchievements
      })
      setIsSubmitted(true)

    } catch (error) {
      console.error('❌ [QuizInterface] Error submitting quiz:', error)
      toast.error('Có lỗi xảy ra khi nộp bài. Vui lòng thử lại.')
      setIsSubmitted(false) // Reset state on error
    } finally {
      isSubmittingRef.current = false
    }
  }

  // Update handleSubmitRef
  useEffect(() => {
    handleSubmitRef.current = handleSubmit
  }, [handleSubmit])

  const toggleFlag = (questionId: string) => {
    setFlaggedQuestions(prev => {
      const newSet = new Set(prev)
      if (newSet.has(questionId)) {
        newSet.delete(questionId)
      } else {
        newSet.add(questionId)
      }
      return newSet
    })
  }

  const toggleExplanation = (questionId: string) => {
    setShowExplanation(prev => ({
      ...prev,
      [questionId]: !prev[questionId]
    }))
  }

  if (isSubmitted) {
    return (
      <QuizSubmission
        quizId={quizData.id || 'quiz'}
        quizData={quizData}
        lessonId={lessonId}
        questions={safeQuestions}
        answers={answers}
        timeSpent={Math.floor((Date.now() - startTime) / 1000)}
        onContinue={onBack}
        onRetry={() => {
          setIsSubmitted(false)
          setSubmissionResult(null) // Reset result
          setCurrentQuestion(0)
          setAnswers({})
          setTimeLeft(quizData.gioi_han_thoi_gian || 900)
          isSubmittingRef.current = false // Reset submitting flag
        }}
        initialResults={submissionResult?.results} // Pass results
        initialAchievements={submissionResult?.achievements} // Pass achievements
      />
    )
  }

  // Helper function: Encode image path để hỗ trợ khoảng trắng và ký tự tiếng Việt
  const encodeImagePath = (imagePath: string | undefined): string | undefined => {
    if (!imagePath) return undefined

    // Split path thành parts, encode từng part (trừ /)
    const parts = imagePath.split('/')
    const encodedParts = parts.map(part => {
      // Không encode phần rỗng hoặc chỉ có /
      if (!part || part === '') return part
      // Encode từng part để xử lý khoảng trắng và ký tự đặc biệt
      return encodeURIComponent(part)
    })

    return encodedParts.join('/')
  }




  const shouldUseMobileLayout = isMobileDevice()
  const shouldUseCompactLayout = isSmallScreen() || shouldUseMobileLayout

  return (
    <div className={`${isDarkMode ? 'fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 overflow-hidden' : 'min-h-screen bg-gradient-to-br from-slate-50 via-blue-50/30 to-indigo-50/30'}`}>
      {/* No wrapper div for fullscreen - use full screen space */}
      {/* Top Header - Fixed - Mobile Optimized */}
      <div className={`fixed top-0 left-0 right-0 ${isDarkMode ? 'bg-slate-800/95' : 'bg-white/95'} backdrop-blur-md shadow-lg border-b ${isDarkMode ? 'border-slate-700' : 'border-slate-200'} z-40`}>
        <div className={`${isDarkMode ? 'w-full' : 'max-w-7xl'} mx-auto px-2 sm:px-3 md:px-4 lg:px-8`}>
          <div className="flex items-center justify-between h-12 sm:h-14 md:h-16">
            <div className="flex items-center space-x-1.5 sm:space-x-2 md:space-x-4 flex-1 min-w-0">
              <button
                onClick={() => {
                  // Exit fullscreen if in fullscreen mode
                  if (document.fullscreenElement) {
                    document.exitFullscreen()
                  }
                  onBack()
                }}
                className={`quiz-header-btn btn-secondary flex items-center space-x-1 sm:space-x-2 px-1.5 sm:px-2 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-md sm:rounded-lg md:rounded-xl text-xs sm:text-sm md:text-base shrink-0 ${isDarkMode ? 'bg-slate-700 text-white hover:bg-slate-600' : ''}`}
              >
                <ArrowLeft className="w-3 h-3 sm:w-4 sm:h-4" />
                <span className="font-semibold hidden sm:inline">Quay lại</span>
                <span className="font-semibold sm:hidden">Về</span>
              </button>
              <div className={`h-3 sm:h-4 md:h-6 w-px ${isDarkMode ? 'bg-slate-600' : 'bg-slate-300'} shrink-0`} />
              <h1 className={`text-xs sm:text-sm md:text-xl font-bold ${isDarkMode ? 'text-white' : 'text-slate-800'} truncate`}>{quizData.ten || 'Bài kiểm tra'}</h1>
            </div>

            <div className="flex items-center space-x-1 sm:space-x-1.5 md:space-x-2 lg:space-x-4 shrink-0">
              {/* Student Name - Compact on mobile */}
              <div className={`flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-md sm:rounded-lg md:rounded-xl border text-xs sm:text-sm md:text-base font-medium ${isDarkMode
                ? 'bg-slate-700 border-slate-600 text-white'
                : 'bg-blue-50 border-blue-200 text-blue-700'
                }`}>
                <User className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 shrink-0" />
                <span className="hidden sm:inline">{getStudentName()}</span>
              </div>

              {/* Submit Button - In Header - Compact on mobile */}
              <button
                onClick={() => setShowConfirmSubmit(true)}
                className={`flex items-center justify-center space-x-1 sm:space-x-2 px-2 sm:px-3 md:px-4 py-1 sm:py-1.5 md:py-2 rounded-md sm:rounded-lg md:rounded-xl transition-all duration-300 font-semibold text-xs sm:text-sm ${isDarkMode
                  ? 'bg-gradient-to-r from-green-600 to-green-700 text-white hover:from-green-700 hover:to-green-800 shadow-md hover:shadow-lg'
                  : 'bg-gradient-to-r from-green-500 to-green-600 text-white hover:from-green-600 hover:to-green-700 shadow-md hover:shadow-lg'
                  }`}
                aria-label="Nộp bài"
                title="Nộp bài"
              >
                <Send className="w-3 h-3 sm:w-4 sm:h-4 shrink-0" />
                <span className="hidden sm:inline">Nộp bài</span>
              </button>

              {/* Timer - Always visible, but styled based on fullscreen mode - Compact on mobile */}
              <div className={`timer-mobile flex items-center space-x-1 sm:space-x-2 px-1.5 sm:px-2 md:px-3 lg:px-5 py-1 sm:py-1.5 md:py-2.5 rounded-md sm:rounded-lg md:rounded-xl ${isDarkMode
                ? 'bg-gradient-to-r from-red-600 to-orange-600 text-white shadow-lg shadow-red-500/50 animate-pulse'
                : 'bg-gradient-to-r from-blue-100 to-indigo-100 text-blue-700'
                }`}>
                <Clock className={`${isDarkMode ? 'w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5' : 'w-3 h-3 sm:w-4 sm:h-4'} shrink-0`} />
                <span className={`font-bold whitespace-nowrap ${isDarkMode ? 'text-xs sm:text-sm md:text-base lg:text-xl' : 'text-xs sm:text-sm md:text-base'}`}>{formatTime(timeLeft)}</span>
              </div>

              <div className={`hidden sm:flex items-center space-x-1.5 md:space-x-2 px-2 md:px-4 py-1.5 md:py-2 rounded-lg md:rounded-xl ${isDarkMode
                ? 'bg-gradient-to-r from-emerald-600 to-emerald-700 text-white'
                : 'bg-gradient-to-r from-emerald-100 to-emerald-200 text-emerald-700'
                } border ${isDarkMode ? 'border-emerald-500' : 'border-emerald-200'}`}>
                <CheckCircle className="w-3 h-3 md:w-4 md:h-4" />
                <span className="font-semibold text-xs md:text-sm">{getAnsweredCount()}/{safeQuestions.length || 0}</span>
              </div>

              {/* Menu Dropdown Button - Hiển thị trên cả Desktop và Mobile - Compact on mobile */}
              <div className="relative">
                <button
                  onClick={() => setShowMenuDropdown(!showMenuDropdown)}
                  className={`quiz-header-btn btn-soft p-1.5 sm:p-2 md:p-2.5 rounded-md sm:rounded-lg md:rounded-xl transition-all duration-200 ${isDarkMode
                    ? 'bg-slate-700 text-white hover:bg-slate-600'
                    : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                    } ${showMenuDropdown ? 'ring-2 ring-blue-500' : ''}`}
                  aria-label="Menu hiển thị"
                  title="Menu"
                >
                  <Menu className="w-3 h-3 sm:w-4 sm:h-4 md:w-5 md:h-5 shrink-0" />
                </button>

                {/* Dropdown Menu */}
                {showMenuDropdown && (
                  <>
                    {/* Backdrop để đóng menu khi click outside */}
                    <div
                      className="fixed inset-0 z-30"
                      onClick={() => setShowMenuDropdown(false)}
                    />
                    {/* Dropdown Content */}
                    <div className={`absolute right-0 mt-2 w-56 rounded-lg shadow-xl border z-40 ${isDarkMode
                      ? 'bg-slate-800 border-slate-700'
                      : 'bg-white border-slate-200'
                      }`}>
                      <div className="py-1">
                        {/* Option 1: Hiện danh sách câu hỏi */}
                        <button
                          onClick={() => {
                            setShowQuestionList(true)
                            setShowMenuDropdown(false)
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors duration-200 ${showQuestionList
                            ? isDarkMode
                              ? 'bg-teal-600 text-white'
                              : 'bg-teal-500 text-white'
                            : isDarkMode
                              ? 'text-slate-200 hover:bg-slate-700'
                              : 'text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          <List className={`w-4 h-4 ${showQuestionList ? '' : 'opacity-60'}`} />
                          <span className="text-sm font-medium">Hiện danh sách câu hỏi</span>
                        </button>

                        {/* Option 2: Ẩn danh sách câu hỏi */}
                        <button
                          onClick={() => {
                            setShowQuestionList(false)
                            setShowMenuDropdown(false)
                          }}
                          className={`w-full flex items-center space-x-3 px-4 py-3 text-left transition-colors duration-200 ${!showQuestionList
                            ? isDarkMode
                              ? 'bg-teal-600 text-white'
                              : 'bg-teal-500 text-white'
                            : isDarkMode
                              ? 'text-slate-200 hover:bg-slate-700'
                              : 'text-slate-700 hover:bg-slate-100'
                            }`}
                        >
                          <Monitor className={`w-4 h-4 ${!showQuestionList ? '' : 'opacity-60'}`} />
                          <span className="text-sm font-medium">Ẩn danh sách câu hỏi</span>
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Question Navigation Panel - Mobile: Below content, Desktop: Right sidebar */}

      {/* Main Content - Dynamic Layout based on showQuestionList */}
      <div className={`${isDarkMode ? 'h-full pt-0 pb-2 md:pb-4 overflow-hidden' : 'pt-16 sm:pt-20 md:pt-24 pb-4 sm:pb-6 md:pb-8'}`}>
        <div className={`${isDarkMode ? 'w-full h-full' : 'max-w-none'} mx-auto px-1 sm:px-2 md:px-4 lg:px-6 ${isDarkMode ? '' : 'h-full'}`}>
          <div className={`grid grid-cols-1 ${showQuestionList ? 'lg:grid-cols-4' : 'lg:grid-cols-1'} gap-1 sm:gap-2 md:gap-4 ${isDarkMode ? 'h-full' : ''}`}>
            {/* Question Content - Show all questions when showQuestionList is true, single question when false */}
            <div
              ref={questionContentRef}
              className={`${showQuestionList ? 'lg:col-span-3' : 'lg:col-span-1'} ${isDarkMode ? 'h-full overflow-y-auto pr-2 pb-4' : 'min-h-[calc(100vh-300px)] overflow-y-auto pb-4'} mobile-scroll ${showQuestionList && isExamTest ? 'quiz-scrollbar-visible' : ''}`}
              style={showQuestionList && isExamTest ? {
                scrollbarWidth: 'thin',
                scrollbarColor: isDarkMode ? '#64748b #1e293b' : '#94a3b8 #f1f5f9'
              } : {}}
            >
              {showQuestionList ? (
                /* Show all questions — câu chùm bọc chung 1 container */
                <div className={`${isDarkMode ? 'p-2 md:p-4' : 'space-y-4 p-2 md:p-4'}`}>
                  {(() => {
                    // Gom câu hỏi thành nhóm: câu đơn = 1 nhóm, câu chùm = 1 nhóm chung
                    type RenderItem =
                      | { kind: 'single'; q: any; index: number }
                      | { kind: 'cluster'; clusterId: string; context: string; items: { q: any; index: number }[] };

                    const groups: RenderItem[] = [];
                    const clusterMap = new Map<string, RenderItem & { kind: 'cluster' }>();

                    safeQuestions.forEach((question: any, index: number) => {
                      const meta = question.examMetadata || {};
                      const cid = meta.cluster_id as string | undefined;
                      if (cid) {
                        if (!clusterMap.has(cid)) {
                          const g: RenderItem & { kind: 'cluster' } = {
                            kind: 'cluster',
                            clusterId: cid,
                            context: meta.cluster_context || '',
                            items: [],
                          };
                          clusterMap.set(cid, g);
                          groups.push(g);
                        }
                        clusterMap.get(cid)!.items.push({ q: question, index });
                      } else {
                        groups.push({ kind: 'single', q: question, index });
                      }
                    });

                    return groups.map((group) => {
                      if (group.kind === 'single') {
                        const { q, index } = group;
                        return (
                          <div
                            key={q.id}
                            ref={(el) => { questionRefs.current[index] = el }}
                            id={`question-${index}`}
                            className="mb-6 scroll-mt-24 md:scroll-mt-32"
                          >
                            <QuizQuestionCard
                              question={q} index={index} totalQuestions={safeQuestions.length}
                              displayNumber={displayNumberMap[index] ?? (index + 1)}
                              isDarkMode={isDarkMode} answer={answers[q.id]}
                              isFlagged={flaggedQuestions.has(q.id)}
                              showExplanation={!!showExplanation[q.id]}
                              showQuestionList={showQuestionList}
                              onAnswerChange={handleAnswerChange} onToggleFlag={toggleFlag}
                              onToggleExplanation={toggleExplanation}
                              onPrevious={goToPreviousQuestion} onNext={goToNextQuestion}
                              hasPrevious={(displayNumberMap[index] ?? (index + 1)) > 1}
                              hasNext={(displayNumberMap[index] ?? (index + 1)) < safeQuestions.length}
                              onUploadStatusChange={setIsUploading}
                            />
                          </div>
                        );
                      }

                      // Cluster group — hiển thị context 1 lần, bọc tất cả câu trong khung
                      const firstIndex = group.items[0]?.index ?? 0;
                      const lastIndex  = group.items[group.items.length - 1]?.index ?? firstIndex;
                      const firstNum   = displayNumberMap[firstIndex] ?? (firstIndex + 1);
                      const lastNum    = displayNumberMap[lastIndex]  ?? (lastIndex  + 1);
                      const builtCtx   = rebuildClusterCtx(group.context, firstNum, lastNum);
                      return (
                        <div
                          key={group.clusterId}
                          ref={(el) => { questionRefs.current[firstIndex] = el }}
                          id={`question-${firstIndex}`}
                          className={`mb-6 scroll-mt-24 md:scroll-mt-32 rounded-2xl border-2 overflow-hidden ${
                            isDarkMode ? 'border-amber-700' : 'border-amber-300'
                          }`}
                        >
                          {/* Context header */}
                          <div className={`p-4 border-b ${isDarkMode ? 'bg-amber-900/30 border-amber-700 text-amber-200' : 'bg-amber-50 border-amber-200 text-amber-800'}`}>
                            <p className="text-[10px] font-black uppercase mb-2 tracking-wide opacity-70">
                              📖 Đọc thông tin sau và trả lời câu {firstNum}{group.items.length > 1 ? ` đến câu ${lastNum}` : ''}:
                            </p>
                            <div className={`text-sm ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>
                              <KaTeXRenderer content={builtCtx} />
                            </div>
                          </div>
                          {/* Questions */}
                          <div className={isDarkMode ? 'bg-slate-800' : 'bg-white'}>
                            {group.items.map(({ q, index }, itemIdx) => (
                              <div
                                key={q.id}
                                id={`question-${index}`}
                                className={`scroll-mt-24 md:scroll-mt-32 ${itemIdx > 0 ? `border-t ${isDarkMode ? 'border-slate-700' : 'border-amber-100'}` : ''}`}
                              >
                                <QuizQuestionCard
                                  question={q} index={index} totalQuestions={safeQuestions.length}
                                  displayNumber={displayNumberMap[index] ?? (index + 1)}
                                  isDarkMode={isDarkMode} answer={answers[q.id]}
                                  isFlagged={flaggedQuestions.has(q.id)}
                                  showExplanation={!!showExplanation[q.id]}
                                  showQuestionList={showQuestionList}
                                  onAnswerChange={handleAnswerChange} onToggleFlag={toggleFlag}
                                  onToggleExplanation={toggleExplanation}
                                  onPrevious={goToPreviousQuestion} onNext={goToNextQuestion}
                                  hasPrevious={(displayNumberMap[index] ?? (index + 1)) > 1}
                                  hasNext={(displayNumberMap[index] ?? (index + 1)) < safeQuestions.length}
                                  onUploadStatusChange={setIsUploading}
                                />
                              </div>
                            ))}
                          </div>
                        </div>
                      );
                    });
                  })()}
                </div>
              ) : (
                /* Show only current question - single question (mobile/tablet) */
                safeQuestions && safeQuestions[currentQuestion] && (() => {
                  const cq    = safeQuestions[currentQuestion];
                  const cMeta = (cq?.examMetadata || {}) as Record<string, any>;
                  const cid   = cMeta.cluster_id as string | undefined;
                  const rawCtx = (cMeta.cluster_context as string) || '';
                  // Tính số câu thực tế của cluster này
                  const { first: cFirst, last: cLast } = cid
                    ? getClusterNums(cid, safeQuestions)
                    : { first: 1, last: 1 };
                  const builtMobileCtx = cid ? rebuildClusterCtx(rawCtx, cFirst, cLast) : '';
                  return (
                  <div className={`${isDarkMode ? 'p-2 md:p-4' : 'p-2'}`}>
                    {cid && rawCtx && (
                      <div className={`mb-3 p-4 rounded-2xl border-2 text-sm ${isDarkMode ? 'bg-amber-900/30 border-amber-700' : 'bg-amber-50 border-amber-300'}`}>
                        <p className={`text-[10px] font-black uppercase mb-2 tracking-wide ${isDarkMode ? 'text-amber-300' : 'text-amber-700'}`}>
                          📖 Đọc thông tin sau và trả lời câu {cFirst}{cLast !== cFirst ? ` đến câu ${cLast}` : ''}:
                        </p>
                        <div className={`text-sm leading-relaxed ${isDarkMode ? 'text-slate-100' : 'text-gray-800'}`}>
                          <KaTeXRenderer content={builtMobileCtx} />
                        </div>
                      </div>
                    )}
                    <QuizQuestionCard
                      question={safeQuestions[currentQuestion]}
                      index={currentQuestion}
                      totalQuestions={safeQuestions.length}
                      displayNumber={displayNumberMap[currentQuestion] ?? (currentQuestion + 1)}
                      isDarkMode={isDarkMode}
                      answer={answers[safeQuestions[currentQuestion].id]}
                      isFlagged={flaggedQuestions.has(safeQuestions[currentQuestion].id)}
                      showExplanation={!!showExplanation[safeQuestions[currentQuestion].id]}
                      showQuestionList={showQuestionList}
                      onAnswerChange={handleAnswerChange}
                      onToggleFlag={toggleFlag}
                      onToggleExplanation={toggleExplanation}
                      onPrevious={goToPreviousQuestion}
                      onNext={goToNextQuestion}
                      hasPrevious={(displayNumberMap[currentQuestion] ?? (currentQuestion + 1)) > 1}
                      hasNext={(displayNumberMap[currentQuestion] ?? (currentQuestion + 1)) < safeQuestions.length}
                      onUploadStatusChange={setIsUploading}
                    />
                  </div>
                  );
                })()
              )}
            </div>

            {/* Mobile Question Navigation Panel - Below content, always show when showQuestionList is true on mobile */}
            {showQuestionList && safeQuestions.length > 0 && (
              <div className="lg:hidden col-span-1 mt-2 mb-2">
                <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} rounded-lg shadow-lg border px-1.5 sm:px-2 py-2 sm:py-2.5 max-h-[280px] overflow-y-auto`}>
                  <div className="max-w-md mx-auto">
                    {groupedSections.length > 0 ? (
                      <div className="space-y-1">
                        {groupedSections.map(section => (
                          <div key={section.title}>
                            <p className={`text-[10px] font-semibold mb-0.5 ${isDarkMode ? 'text-slate-200' : 'text-slate-600'}`}>
                              {section.title}
                            </p>
                            <div
                              className="flex items-center gap-1.5 overflow-x-auto no-scrollbar py-0.5"
                              style={{ whiteSpace: 'nowrap', WebkitOverflowScrolling: 'touch' }}
                            >
                              {section.indices.map(index => renderQuestionDot(index, 'mobile', displayNumberMap[index]))}
                            </div>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-2">
                        <p className={`text-xs ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                          Đang tải...
                        </p>
                      </div>
                    )}

                    {/* Legend - Compact - Mobile: All in one row */}
                    <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-200'}`}>
                      <div className="flex items-center justify-between space-x-1 text-[10px]">
                        <div className="flex items-center space-x-0.5">
                          <div className="w-2 h-2 rounded-full bg-gradient-to-br from-blue-500 to-blue-600"></div>
                          <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Làm</span>
                        </div>
                        <div className="flex items-center space-x-0.5">
                          <div className="w-2 h-2 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600"></div>
                          <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Xong</span>
                        </div>
                        <div className="flex items-center space-x-0.5">
                          <div className="w-2 h-2 rounded-full bg-gradient-to-br from-slate-200 to-slate-300"></div>
                          <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Chưa</span>
                        </div>
                        <div className="flex items-center space-x-0.5">
                          <span className="text-[10px]">🚩</span>
                          <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>Dấu</span>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Desktop Sidebar - Scrollable question list - Only show when showQuestionList is true */}
            {showQuestionList && safeQuestions.length > 0 && (
              <div className="hidden lg:block lg:col-span-1">
                <div className={`${isDarkMode ? 'bg-slate-800 border-slate-700' : 'bg-white border-slate-200'} rounded-xl shadow-lg border p-2 sm:p-3 sticky top-24 max-h-[calc(100vh-120px)] overflow-y-auto`}>

                  {/* Dots Grid - 6 columns per row - Scrollable */}
                  {groupedSections.length > 0 ? (
                    <div className="space-y-4">
                      {groupedSections.map(section => (
                        <div key={section.title}>
                          <p className={`text-xs font-semibold mb-2 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
                            {section.title}
                          </p>
                          <div className="quiz-dots-container">
                            {section.indices.map(index => renderQuestionDot(index, 'desktop', displayNumberMap[index]))}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <p className={`text-sm ${isDarkMode ? 'text-slate-400' : 'text-slate-500'}`}>
                        Đang tải danh sách câu hỏi...
                      </p>
                    </div>
                  )}

                  {/* Legend - Match quiz dots colors */}
                  <div className={`mt-2 pt-2 border-t ${isDarkMode ? 'border-slate-600' : 'border-slate-200'} space-y-1 text-xs`}>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded-full bg-gradient-to-br from-blue-500 to-blue-700"></div>
                        <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'} text-xs font-medium`}>Làm</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded-full bg-gradient-to-br from-emerald-500 to-emerald-600"></div>
                        <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'} text-xs font-medium`}>Xong</span>
                      </div>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-1">
                        <div className="w-3 h-3 rounded-full bg-white border-2 border-gray-400"></div>
                        <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'} text-xs font-medium`}>Chưa</span>
                      </div>
                      <div className="flex items-center space-x-1">
                        <span className="text-sm">🚩</span>
                        <span className={`${isDarkMode ? 'text-slate-300' : 'text-slate-700'} text-xs font-medium`}>Dấu</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* ✅ ANTI-CHEAT: Initial Warning Modal (hiển thị trước khi bắt đầu) */}
      {showAntiCheatWarning && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[9999]">
          <div className={`${isDarkMode ? 'bg-slate-800 border-yellow-500' : 'bg-white border-yellow-500'} rounded-2xl shadow-2xl p-8 max-w-lg mx-4 border-4`}>
            <div className="text-center">
              <div className="w-20 h-20 bg-gradient-to-r from-yellow-500 to-orange-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <AlertTriangle className="w-10 h-10 text-white" />
              </div>
              <h3 className={`text-2xl font-bold ${isDarkMode ? 'text-yellow-400' : 'text-yellow-600'} mb-3`}>⚠️ LƯU Ý QUAN TRỌNG</h3>
              <div className={`${isDarkMode ? 'text-slate-100' : 'text-slate-700'} mb-4 text-left space-y-3`}>
                <p className={`font-semibold text-lg ${isDarkMode ? 'text-white' : 'text-slate-800'}`}>
                  🔒 Bài kiểm tra yêu cầu chế độ toàn màn hình để ngăn gian lận
                </p>
                <p className={`text-base ${isDarkMode ? 'text-yellow-200' : 'text-slate-700'}`}>
                  📱 <strong>Trên mobile:</strong> Không được chuyển ứng dụng hoặc bật 2 màn hình
                </p>
                <p className={`text-base ${isDarkMode ? 'text-yellow-200' : 'text-slate-700'}`}>
                  💻 <strong>Trên máy tính:</strong> Không được thoát chế độ toàn màn hình
                </p>
                <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-red-900/50 border-red-400' : 'bg-red-50 border-red-500'} border-2 mt-4`}>
                  <p className={`font-bold ${isDarkMode ? 'text-red-300' : 'text-red-600'}`}>
                    ⚡ Vi phạm 3 lần → Tự động nộp bài!
                  </p>
                </div>
              </div>
              <p className={`text-sm ${isDarkMode ? 'text-slate-300' : 'text-slate-500'} mb-6 italic`}>
                Hệ thống sẽ theo dõi và ghi nhận số lần vi phạm vào lịch sử học tập
              </p>
              <button
                onClick={() => {
                  setShowAntiCheatWarning(false);
                  // Request fullscreen NGAY từ user gesture
                  console.log('🎯 User clicked start - requesting fullscreen...');
                }}
                className="bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white px-8 py-3 rounded-xl font-semibold shadow-lg w-full"
              >
                ✓ Tôi đã hiểu, bắt đầu làm bài
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Submit Confirmation Modal */}
      {showConfirmSubmit && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="bg-white rounded-2xl shadow-2xl p-8 max-w-md mx-4 border-2 border-slate-200">
            <div className="text-center">
              <div className="w-16 h-16 bg-gradient-to-r from-emerald-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Send className="w-8 h-8 text-white" />
              </div>
              <h3 className="text-xl font-bold text-slate-800 mb-2">Xác nhận nộp bài</h3>
              <p className="text-slate-600 mb-6">
                Bạn đã trả lời {getAnsweredCount()}/{safeQuestions.length || 0} câu hỏi.
                Bạn có chắc chắn muốn nộp bài không?
              </p>
              {exitCount > 0 && (
                <p className="text-red-600 text-sm mb-4 font-semibold">
                  ⚠️ Bạn đã thoát màn hình {exitCount} lần
                </p>
              )}
              <div className="flex space-x-4">
                <button
                  onClick={() => setShowConfirmSubmit(false)}
                  className="btn-secondary flex-1 px-4 py-2 rounded-xl"
                >
                  Hủy
                </button>
                <button
                  onClick={handleSubmit}
                  className="btn-primary flex-1 px-4 py-2 rounded-xl"
                >
                  Nộp bài
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ✅ ANTI-CHEAT: Thoát fullscreen = Tự động nộp bài */}

      {/* Achievement Notifications */}
      {newAchievements.length > 0 && (
        <AchievementNotification
          achievements={newAchievements}
          onClose={() => setNewAchievements([])}
        />
      )}
      {/* ✅ Modal yêu cầu đăng nhập với Google - Không cho đóng nếu chưa đăng nhập */}
      {showLoginModal && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[10000] p-4"
          onClick={(e) => {
            // Không cho đóng khi click ra ngoài
            e.stopPropagation();
          }}
        >
          <div
            className="bg-white rounded-2xl p-8 max-w-md w-full mx-4 shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* ✅ Không hiện nút X - bắt buộc phải đăng nhập */}
            <div className="flex justify-end items-center mb-6">
              <div className="w-8 h-8"></div> {/* Spacer để giữ layout */}
            </div>

            <div className="text-center">
              <h2 className="text-2xl font-bold text-gray-900 mb-2">Yêu cầu đăng nhập</h2>
              <p className="text-gray-600 mb-6">
                Vui lòng đăng nhập với Google để làm bài kiểm tra
              </p>

              {/* Google Sign-In Button Container */}
              {!googleSignInFailed && (
                <div ref={googleButtonRef} id="google-signin-button-quiz" className="flex justify-center mb-4"></div>
              )}

              {/* Fallback khi Google Sign-In không khả dụng (Zalo / trình duyệt in-app) */}
              {googleSignInFailed && (
                <div className="flex flex-col gap-3 mb-4">
                  <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-center">
                    <div className="text-3xl mb-2">🌐</div>
                    <p className="text-sm font-semibold text-blue-800 mb-1">
                      Đang mở Chrome{chromeCountdown > 0 ? ` trong ${chromeCountdown}...` : '...'}
                    </p>
                    <p className="text-xs text-blue-600">Zalo không hỗ trợ đăng nhập Google</p>
                  </div>
                  <button
                    onClick={() => {
                      const url = window.location.href;
                      const ua = navigator.userAgent;
                      if (/Android/i.test(ua)) {
                        const fallback = encodeURIComponent('https://play.google.com/store/apps/details?id=com.android.chrome');
                        window.location.href = `intent://${url.replace(/^https?:\/\//, '')}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallback};end`;
                      } else if (/iPhone|iPad|iPod/i.test(ua)) {
                        window.location.href = url.replace(/^https?/, 'googlechrome');
                      } else {
                        window.open(url, '_blank');
                      }
                    }}
                    className="w-full bg-blue-600 hover:bg-blue-700 text-white font-semibold py-3 px-4 rounded-xl text-sm transition-colors"
                  >
                    🌐 Mở trong Chrome ngay
                  </button>
                  <button
                    onClick={() => {
                      navigator.clipboard?.writeText(window.location.href).then(() => setLinkCopied(true));
                    }}
                    className="w-full bg-gray-100 hover:bg-gray-200 text-gray-700 font-medium py-2 px-4 rounded-xl text-sm transition-colors"
                  >
                    {linkCopied ? '✅ Đã sao chép!' : '📋 Sao chép link để dán vào Chrome'}
                  </button>
                </div>
              )}

              {!googleSignInFailed && (
                <p className="text-xs text-gray-500">
                  Sau khi đăng nhập, bạn sẽ được yêu cầu bổ sung thông tin nếu cần
                </p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default QuizInterface
